<template>
    <div ref="root" class="blog-pagination">
        <slot />

        <p v-if="errorMessage" class="blog-pagination__error" style="color: red;">
            {{ errorMessage }}
        </p>

        <button
            v-if="hasMore"
            type="button"
            class="btn blue-solid blog-pagination__button"
            :disabled="isLoading"
            @click="loadMore"
        >
            <span :data-text="buttonText">{{ buttonText }}</span>
        </button>
    </div>
</template>
<script setup>
import { computed, createApp, getCurrentInstance, nextTick, onMounted, ref } from 'vue';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Stickers from './Stickers.vue';

const props = defineProps({
    endpoint: {
        type: String,
        required: true,
    },
    initialHasMore: {
        type: [Number, String],
        default: 0,
    },
    perPage: {
        type: [Number, String],
        default: 3,
    },
});

const root = ref(null);
const hasMore = ref(normalizeBoolean(props.initialHasMore));
const isLoading = ref(false);
const errorMessage = ref('');
const start = ref(0);
const instance = getCurrentInstance();

const buttonText = computed(() => (isLoading.value ? 'Loading' : 'Read More'));
const pageLength = computed(() => Math.max(1, parseInt(props.perPage, 10) || 3));

onMounted(() => {
    start.value = getCurrentPostCount();
});

async function loadMore() {
    const list = getPostList();

    if (!list || isLoading.value || !hasMore.value) {
        return;
    }

    isLoading.value = true;
    errorMessage.value = '';

    try {
        const response = await fetch(buildUrl(), {
            headers: {
                Accept: 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();
        const addedCount = appendPosts(list, data.html || '');

        start.value = Number.isFinite(Number(data.nextStart))
            ? Number(data.nextStart)
            : start.value + addedCount;
        hasMore.value = normalizeBoolean(data.hasMore);

        await nextTick();
        refreshLayout();
    } catch (error) {
        errorMessage.value = 'Sorry, the next posts could not be loaded.';
        console.error(error);
    } finally {
        isLoading.value = false;
    }
}

function buildUrl() {
    const url = new URL(props.endpoint, window.location.origin);
    url.searchParams.set('start', start.value.toString());
    url.searchParams.set('limit', pageLength.value.toString());
    console.log(url);
    return url.toString();
}

function appendPosts(list, html) {
    if (!html.trim()) {
        hasMore.value = false;
        return 0;
    }

    const template = document.createElement('template');
    template.innerHTML = html.trim();

    const posts = Array.from(template.content.children);
    posts.forEach((post) => {
        list.appendChild(post);
        mountStickerComponents(post);
    });

    return posts.length;
}

function getCurrentPostCount() {
    return root.value?.querySelectorAll('[data-blog-post]').length || 0;
}

function getPostList() {
    return root.value?.querySelector('[data-blog-post-list]') || null;
}

function normalizeBoolean(value) {
    return value === true || value === 1 || value === '1' || value === 'true';
}

function refreshLayout() {
    requestAnimationFrame(() => {
        instance?.proxy?.lenis?.resize?.();
        ScrollTrigger.refresh();
    });
}

function mountStickerComponents(scope) {
    scope.querySelectorAll('stickers:not([data-stickers-mounted])').forEach((element) => {
        element.dataset.stickersMounted = 'true';

        createApp(Stickers, {
            max: element.getAttribute('max') || 3,
        }).mount(element);
    });
}
</script>
